import { CssBaseline, ThemeProvider } from "@mui/material";
import { AuthProvider } from "context/AuthContext";
import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import theme from "theme";
import App from "./App";
import "./components/i18n";
import { AppDataProvider } from "./context/index";
import "./index.css";

// Lazy load Matomo to improve mobile performance
let MatomoProvider = null;
let matomoInstance = null;

// Function to initialize Matomo asynchronously
const initMatomo = async () => {
  if (MatomoProvider) return; // Already initialized
  
  const { createInstance, MatomoProvider: MP } = await import("@jonkoops/matomo-tracker-react");
  
  MatomoProvider = MP;
  matomoInstance = createInstance({
    urlBase: import.meta.env.VITE_MATOMO_SITE,
    siteId: import.meta.env.VITE_MATOMO_SITE_ID,
    disabled: false,
    linkTracking: false,
    configurations: {
      disableCookies: true,
      setSecureCookie: true,
      setRequestMethod: "POST",
    },
  });
};

// Wrapper component that handles Matomo loading
function AppWithMatomo() {
  const [matomoReady, setMatomoReady] = useState(false);

  useEffect(() => {
    // Load Matomo after a delay to prioritize critical resources
    const timer = setTimeout(() => {
      initMatomo().then(() => {
        setMatomoReady(true);
      });
    }, 3000); // Delay Matomo loading by 3 seconds

    return () => clearTimeout(timer);
  }, []);

  if (!matomoReady || !MatomoProvider) {
    // Render app without Matomo initially
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AppDataProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </AppDataProvider>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  // Render with Matomo once loaded
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppDataProvider>
          <AuthProvider>
            <MatomoProvider value={matomoInstance}>
              <App />
            </MatomoProvider>
          </AuthProvider>
        </AppDataProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

const root = createRoot(document.getElementById("root"));
root.render(
  <StrictMode>
    <AppWithMatomo />
  </StrictMode>
);

